import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  ShoppingBag,
  Search,
  User,
  Menu,
  ArrowRight,
  Truck,
  RotateCcw,
  ShieldCheck,
  CreditCard,
  MapPin,
  Mail,
  Trash2,
  Plus,
  Minus,
  Loader2,
  Car,
  Wrench,
  Phone,
  Heart,
  GitCompareArrows,
  PackageX,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useCurrencyFormat } from "@/hooks/useCurrencyFormat";
import { useCart } from "../contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import { useOrganization } from "@/contexts/OrganizationContext";
import { fetchProductAvailability } from "../services/storefront-service";
import type { ResolvedStorefrontTheme } from "../services/storefront-web-service";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

const DEFAULT_THEME: ResolvedStorefrontTheme = {
  brandVariant: "default",
  primaryColor: "#0f172a",
  accentColor: "#4f46e5",
  headingFont: "Inter Tight",
  bodyFont: "Inter",
  cardRadius: 30,
  navigationStyle: "minimal",
  storeDisplayName: "Store",
  storeTagline: "",
  heroBadge: "Welcome",
  heroTitle: "Shop",
  heroLineHighlight: "with us",
  heroSubtitle: "Browse our catalogue.",
  heroCtaPrimary: "Shop now",
  heroCtaSecondary: "Learn more",
  featureTitles: ["Free shipping", "Easy returns", "Secure payment", "Flexible pay"],
  footerBlurb: "",
  contactEmail: "—",
  contactLocation: "—",
  contactPhone: "",
};

export interface StorefrontSectionVisibility {
  hero: boolean;
  features: boolean;
  products: boolean;
  footer: boolean;
}

export interface StorefrontProps {
  categories: string[];
  featuredProducts: any[];
  viewMode: string;
  /** Resolved theme from org settings (mergeStorefrontTheme). */
  theme?: ResolvedStorefrontTheme;
  /** When true, skip inventory RPC (demo / placeholder products). */
  skipStockCheck?: boolean;
  sectionVisibility?: Partial<StorefrontSectionVisibility>;
  /** When true, customers can add out-of-stock items to cart (backorders). */
  allowBackorders?: boolean;
  /** Feature flags from ecommerce settings. */
  enableWishlist?: boolean;
  enableProductComparison?: boolean;
  enableRelatedProducts?: boolean;
}

const DEFAULT_VISIBILITY: StorefrontSectionVisibility = {
  hero: true,
  features: true,
  products: true,
  footer: true,
};

export function StorefrontPreview({
  categories,
  featuredProducts,
  viewMode,
  theme: themeProp,
  skipStockCheck = false,
  sectionVisibility: visProp,
  allowBackorders = false,
  enableWishlist = false,
  enableProductComparison = false,
}: StorefrontProps) {
  const theme = { ...DEFAULT_THEME, ...themeProp };
  const vis = { ...DEFAULT_VISIBILITY, ...visProp };
  const isMotors = theme.brandVariant === "motors";
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();
  const { cart, addToCart, removeFromCart, updateQuantity, total, itemCount, clearCart } = useCart();
  const { formatCurrency } = useCurrencyFormat();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [addingProductId, setAddingProductId] = useState<string | null>(null);

  const motorIcons = [Truck, ShieldCheck, Wrench, CreditCard];
  const defaultIcons = [Truck, RotateCcw, ShieldCheck, CreditCard];
  const iconSet = isMotors ? motorIcons : defaultIcons;
  const featureStrip = theme.featureTitles.map((title, i) => ({
    icon: iconSet[i % iconSet.length],
    title,
  }));

  const handleAddToCart = async (product: {
    id: string;
    name: string;
    price: number;
    image: string;
    category: string;
  }) => {
    if (!currentOrganization?.id) {
      toast({ title: "Error", description: "Store not loaded.", variant: "destructive" });
      return;
    }
    if (skipStockCheck) {
      addToCart(product);
      return;
    }
    setAddingProductId(product.id);
    try {
      const available = await fetchProductAvailability(currentOrganization.id, product.id);
      if (available <= 0 && !allowBackorders) {
        toast({
          title: "Out of stock",
          description: `${product.name} is not available.`,
          variant: "destructive",
        });
        return;
      }
      if (!allowBackorders) {
        const inCart = cart.find((i) => i.id === product.id);
        if (inCart && inCart.quantity >= available) {
          toast({
            title: "Limit reached",
            description: `Only ${available} left in stock.`,
            variant: "destructive",
          });
          return;
        }
      }
      addToCart(product);
    } finally {
      setAddingProductId(null);
    }
  };

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate("/shop/checkout");
  };

  const accentStyle = { backgroundColor: theme.accentColor } as React.CSSProperties;
  const accentText = { color: theme.accentColor } as React.CSSProperties;
  const radius = theme.cardRadius;

  return (
    <div
      className="bg-white min-h-screen font-sans text-slate-900"
      style={{ fontFamily: theme.bodyFont ? `${theme.bodyFont}, ui-sans-serif, system-ui` : undefined }}
    >
      <header
        className={cn(
          "border-b sticky top-0 backdrop-blur-md z-40",
          isMotors ? "border-slate-200 bg-slate-950/95 text-white" : "border-slate-100 bg-white/80",
        )}
      >
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div
                className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                style={accentStyle}
              >
                {isMotors ? <Car className="h-5 w-5" /> : <ShoppingBag className="h-5 w-5" />}
              </div>
              <div className="leading-tight min-w-0">
                <span
                  className={cn(
                    "text-lg font-black tracking-tight block truncate max-w-[160px] sm:max-w-xs",
                    isMotors ? "text-white" : "text-slate-900",
                  )}
                >
                  {theme.storeDisplayName}
                </span>
                {theme.storeTagline ? (
                  <span
                    className={cn(
                      "block text-[9px] font-bold uppercase tracking-widest truncate max-w-[200px]",
                      isMotors ? "text-slate-400" : "text-slate-500",
                    )}
                  >
                    {theme.storeTagline.replace(/^https?:\/\//, "").slice(0, 40)}
                  </span>
                ) : null}
              </div>
            </div>

            <nav className={cn("items-center gap-4", viewMode === "desktop" ? "flex" : "hidden")}>
              {categories.slice(0, 6).map((cat) => (
                <span
                  key={cat}
                  className={cn(
                    "text-xs font-bold transition-colors cursor-default",
                    isMotors ? "text-slate-300" : "text-slate-600",
                  )}
                  style={isMotors ? undefined : ({ "--hover": theme.accentColor } as React.CSSProperties)}
                >
                  {cat}
                </span>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {viewMode === "desktop" && (
              <div
                className={cn(
                  "flex items-center rounded-full px-3 py-1.5 border",
                  isMotors ? "bg-slate-800/80 border-slate-700" : "bg-slate-100 border-transparent",
                )}
              >
                <Search className={cn("h-3.5 w-3.5 mr-2", isMotors ? "text-slate-500" : "text-slate-400")} />
                <span className={cn("text-xs w-28 md:w-36 truncate", isMotors ? "text-slate-500" : "text-slate-400")}>
                  Search products…
                </span>
              </div>
            )}

            <div className="flex items-center gap-1">
              <Tooltip><TooltipTrigger asChild><Button
                variant="ghost"
                size="icon"
                className={cn("h-8 w-8 rounded-full", isMotors && "text-slate-300 hover:text-white hover:bg-slate-800")}
                aria-label="Account"
              >
                <User className="h-4 w-4" />
              </Button></TooltipTrigger><TooltipContent>Account</TooltipContent></Tooltip>
              <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
                <Tooltip><TooltipTrigger asChild><SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn("h-8 w-8 rounded-full relative", isMotors && "text-slate-300 hover:text-white hover:bg-slate-800")}
                    aria-label={`Cart${itemCount ? `, ${itemCount} items` : ""}`}
                  >
                    <ShoppingBag className="h-4 w-4" />
                    {itemCount > 0 && (
                      <span
                        className="absolute top-1 right-1 h-3.5 w-3.5 text-white text-[8px] font-bold rounded-full flex items-center justify-center border border-white animate-in zoom-in"
                        style={accentStyle}
                      >
                        {itemCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger></TooltipTrigger><TooltipContent>Cart</TooltipContent></Tooltip>
                <SheetContent className="w-full sm:max-w-md p-0 flex flex-col h-full bg-white border-l border-slate-100 shadow-2xl">
                  <SheetHeader className="p-6 border-b border-slate-50">
                    <div className="flex items-center justify-between">
                      <SheetTitle className="text-xl font-black tracking-tight">Your cart</SheetTitle>
                      {itemCount > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearCart}
                          className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-red-500"
                        >
                          Clear all
                        </Button>
                      )}
                    </div>
                  </SheetHeader>

                  <ScrollArea className="flex-1 px-6">
                    {cart.length === 0 ? (
                      <div className="h-[60vh] flex flex-col items-center justify-center text-center space-y-4">
                        <div className="h-16 w-16 bg-slate-50 rounded-2xl flex items-center justify-center">
                          <ShoppingBag className="h-8 w-8 text-slate-200" />
                        </div>
                        <div className="space-y-1">
                          <p className="font-bold text-slate-900">Your cart is empty</p>
                          <p className="text-xs text-slate-400">Add products from the catalogue.</p>
                        </div>
                        <Button onClick={() => setIsCartOpen(false)} className="rounded-xl font-bold text-white" style={accentStyle}>
                          Start shopping
                        </Button>
                      </div>
                    ) : (
                      <div className="py-6 space-y-6">
                        {cart.map((item) => (
                          <div key={item.id} className="flex gap-4 group">
                            <div
                              className={cn(
                                "h-20 w-20 rounded-2xl flex items-center justify-center shrink-0 border overflow-hidden",
                                "bg-slate-50 border-slate-100",
                              )}
                              style={{ borderRadius: Math.min(radius, 24) }}
                            >
                              {typeof item.image === "string" && item.image.startsWith("http") ? (
                                <img src={item.image} alt="" className="h-full w-full object-cover" />
                              ) : (
                                <span className="text-3xl" aria-hidden>
                                  {item.image}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 space-y-1">
                              <div className="flex items-start justify-between">
                                <h4 className="font-bold text-sm text-slate-900 leading-tight pr-4">{item.name}</h4>
                                <Tooltip><TooltipTrigger asChild><Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeFromCart(item.id)}
                                  className="h-6 w-6 text-slate-300 hover:text-red-500 -mt-1"
                                  aria-label={`Remove ${item.name}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button></TooltipTrigger><TooltipContent>Remove</TooltipContent></Tooltip>
                              </div>
                              <p className="text-[10px] font-bold uppercase tracking-widest" style={accentText}>
                                {item.category}
                              </p>
                              <div className="flex items-center justify-between mt-3">
                                <div className="flex items-center bg-slate-50 rounded-lg p-1 border border-slate-100">
                                  <button
                                    type="button"
                                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                    className="p-1 text-slate-400 hover:text-slate-900 disabled:opacity-30"
                                    disabled={item.quantity <= 1}
                                    aria-label="Decrease quantity"
                                  >
                                    <Minus className="h-3 w-3" />
                                  </button>
                                  <span className="w-8 text-center text-xs font-bold">{item.quantity}</span>
                                  <button
                                    type="button"
                                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                    className="p-1 text-slate-400 hover:text-slate-900"
                                    aria-label="Increase quantity"
                                  >
                                    <Plus className="h-3 w-3" />
                                  </button>
                                </div>
                                <span className="font-black text-sm text-slate-900">
                                  {formatCurrency(item.price * item.quantity)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>

                  {cart.length > 0 && (
                    <SheetFooter className="p-6 bg-white border-t border-slate-50">
                      <div className="w-full space-y-4">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs font-medium text-slate-400">
                            <span>Subtotal</span>
                            <span>{formatCurrency(total)}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs font-medium text-slate-400">
                            <span>Estimated shipping</span>
                            <span className="text-emerald-600 font-bold">Per checkout</span>
                          </div>
                          <Separator className="my-2 bg-slate-50" />
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-slate-900">Total</span>
                            <span className="text-xl font-black text-slate-900">{formatCurrency(total)}</span>
                          </div>
                        </div>
                        <Button
                          onClick={handleCheckout}
                          className="w-full h-12 text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 group"
                          style={accentStyle}
                        >
                          Checkout
                          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </div>
                    </SheetFooter>
                  )}
                </SheetContent>
              </Sheet>
            </div>

            {viewMode !== "desktop" && (
              <Tooltip><TooltipTrigger asChild><Button
                variant="ghost"
                size="icon"
                className={cn("h-8 w-8 rounded-full", isMotors && "text-slate-200 hover:bg-slate-800")}
                aria-label="Menu"
              >
                <Menu className="h-5 w-5" />
              </Button></TooltipTrigger><TooltipContent>Menu</TooltipContent></Tooltip>
            )}
          </div>
        </div>
      </header>

      {vis.hero && (
        <section className="relative py-12 md:py-20 overflow-hidden px-4 md:px-8">
          <div
            className={cn(
              "absolute top-0 right-0 w-1/2 h-full rounded-l-[50px] md:rounded-l-[100px] -z-10",
              isMotors ? "bg-gradient-to-l from-red-50 to-slate-100" : "bg-indigo-50",
            )}
            style={!isMotors ? { background: `${theme.accentColor}18` } : undefined}
          />
          <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="space-y-6 md:space-y-8">
              <Badge
                className={cn(
                  "border-none px-3 py-1 text-[10px] font-bold rounded-full max-w-full line-clamp-2 text-left h-auto min-h-7",
                  isMotors && "bg-slate-900 text-white",
                )}
                style={!isMotors ? { background: `${theme.accentColor}22`, color: theme.accentColor } : undefined}
              >
                {theme.heroBadge}
              </Badge>
              <h1
                className={cn(
                  "font-black tracking-tight leading-[1.1] text-slate-900",
                  viewMode === "mobile" ? "text-4xl" : "text-5xl md:text-6xl",
                )}
                style={{ fontFamily: theme.headingFont ? `${theme.headingFont}, ui-sans-serif` : undefined }}
              >
                {theme.heroTitle}
                <br />
                <span style={accentText}>{theme.heroLineHighlight}</span>
              </h1>
              <p className="text-sm md:text-base text-slate-500 max-w-lg leading-relaxed">{theme.heroSubtitle}</p>
              <div className="flex flex-wrap gap-2 md:gap-4">
                <Button
                  className="h-10 md:h-12 px-6 text-sm font-bold rounded-xl shadow-lg text-white"
                  style={accentStyle}
                  onClick={() => document.getElementById("featured-products")?.scrollIntoView({ behavior: "smooth" })}
                >
                  {theme.heroCtaPrimary} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button variant="outline" className={cn("h-10 md:h-12 px-6 text-sm font-bold rounded-xl")}>
                  {theme.heroCtaSecondary}
                </Button>
              </div>
            </div>

            <div className={cn("relative flex justify-center", viewMode === "mobile" && "mt-8")}>
              <div
                className={cn(
                  "aspect-square rounded-[40px] rotate-3 shadow-2xl flex items-center justify-center text-[80px] md:text-[120px] text-white",
                  viewMode === "mobile" ? "w-48 h-48" : "w-64 h-64 md:w-80 md:h-80",
                )}
                style={{
                  background: `linear-gradient(to bottom right, ${theme.primaryColor}, ${theme.accentColor})`,
                  borderRadius: radius,
                }}
              >
                {isMotors ? "⚡" : "🛍️"}
              </div>
            </div>
          </div>
        </section>
      )}

      {vis.features && (
        <section className="py-12 bg-slate-50 px-4 md:px-8">
          <div className="container mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {featureStrip.map((feature, i) => (
              <div
                key={i}
                className="flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-3 p-4 bg-white rounded-2xl shadow-sm border border-slate-100"
                style={{ borderRadius: Math.min(radius, 20) }}
              >
                <div
                  className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: `${theme.accentColor}15`, color: theme.accentColor }}
                >
                  <feature.icon className="h-4 w-4" />
                </div>
                <div className="text-center md:text-left">
                  <h4 className="font-bold text-[11px] md:text-xs text-slate-900">{feature.title}</h4>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {vis.products && (
        <section id="featured-products" className="py-16 px-4 md:px-8">
          <div className="container mx-auto">
            <div className="flex items-end justify-between mb-8">
              <h2 className="text-2xl font-black tracking-tight">
                {isMotors ? "Parts, charging & accessories" : "Featured products"}
              </h2>
            </div>

            <div
              className={cn("grid gap-6", viewMode === "mobile" ? "grid-cols-1" : "grid-cols-2 lg:grid-cols-4")}
            >
              {featuredProducts.map((product) => {
                const isOOS = !skipStockCheck && (product.stock ?? 0) <= 0;
                const canAdd = !isOOS || allowBackorders;
                return (
                  <div key={product.id} className="group">
                    <Card
                      className="border-none shadow-sm bg-slate-50 overflow-hidden relative"
                      style={{ borderRadius: radius }}
                    >
                      <div className={cn(
                        "aspect-square flex items-center justify-center transition-transform overflow-hidden relative",
                        isOOS ? "opacity-60 grayscale" : "group-hover:scale-110",
                      )}>
                        {typeof product.image === "string" && product.image.startsWith("http") ? (
                          <img src={product.image} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-6xl" aria-hidden>
                            {product.image}
                          </span>
                        )}
                      </div>
                      {isOOS && (
                        <div className="absolute top-3 left-3 z-20 flex items-center gap-1.5 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shadow-lg">
                          <PackageX className="h-3 w-3" />
                          Out of stock
                        </div>
                      )}
                      <div
                        className="p-4 bg-white shadow-lg relative z-10"
                        style={{ borderTopLeftRadius: radius, borderTopRightRadius: radius }}
                      >
                        <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={accentText}>
                          {product.category}
                        </p>
                        <h3 className="font-bold text-sm text-slate-900 mb-2 line-clamp-2">{product.name}</h3>
                        <div className="flex items-center justify-between gap-2">
                          <span className={cn("text-lg font-black", isOOS ? "text-slate-400" : "text-slate-900")}>
                            {formatCurrency(product.price)}
                          </span>
                          <div className="flex items-center gap-1">
                            {enableWishlist && (
                              <Tooltip><TooltipTrigger asChild><Button
                                size="icon"
                                variant="ghost"
                                className="h-9 w-9 rounded-xl text-slate-400 hover:text-red-500 shrink-0"
                                aria-label={`Add ${product.name} to wishlist`}
                              >
                                <Heart className="h-4 w-4" />
                              </Button></TooltipTrigger><TooltipContent>Add to wishlist</TooltipContent></Tooltip>
                            )}
                            {enableProductComparison && (
                              <Tooltip><TooltipTrigger asChild><Button
                                size="icon"
                                variant="ghost"
                                className="h-9 w-9 rounded-xl text-slate-400 hover:text-indigo-500 shrink-0"
                                aria-label={`Compare ${product.name}`}
                              >
                                <GitCompareArrows className="h-4 w-4" />
                              </Button></TooltipTrigger><TooltipContent>Compare</TooltipContent></Tooltip>
                            )}
                            <Tooltip><TooltipTrigger asChild><Button
                              size="icon"
                              className={cn(
                                "h-9 w-9 rounded-xl text-white shrink-0",
                                !canAdd && "opacity-40 cursor-not-allowed",
                              )}
                              style={{ backgroundColor: theme.primaryColor }}
                              onClick={() => canAdd && handleAddToCart(product)}
                              disabled={!canAdd || addingProductId === product.id}
                              aria-label={isOOS ? `${product.name} is out of stock` : `Add ${product.name} to cart`}
                            >
                              {addingProductId === product.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <ShoppingBag className="h-4 w-4" />
                              )}
                            </Button></TooltipTrigger><TooltipContent>Add to cart</TooltipContent></Tooltip>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {vis.footer && (
        <footer className={cn("text-white py-12 px-4 md:px-8", isMotors ? "bg-slate-950" : "")} style={!isMotors ? { backgroundColor: theme.primaryColor } : undefined}>
          <div className="container mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white" style={accentStyle}>
                  {isMotors ? <Car className="h-5 w-5" /> : <ShoppingBag className="h-5 w-5" />}
                </div>
                <span className="text-lg font-black tracking-tight">{theme.storeDisplayName}</span>
              </div>
              <p className="text-slate-400 text-xs leading-relaxed">{theme.footerBlurb}</p>
            </div>

            <div className="hidden lg:block">
              <h4 className="font-bold text-sm mb-4">Links</h4>
              <ul className="space-y-2 text-slate-400 text-xs">
                <li>Shipping</li>
                <li>Returns</li>
                <li>Contact</li>
              </ul>
            </div>

            <div className="hidden lg:block">
              <h4 className="font-bold text-sm mb-4">Contact</h4>
              <ul className="space-y-2 text-slate-400 text-xs">
                <li className="flex items-start gap-2">
                  <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" style={accentText} />
                  <span>{theme.contactLocation}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 shrink-0" style={accentText} />
                  <a href={theme.contactEmail.includes("@") ? `mailto:${theme.contactEmail}` : undefined} className="hover:underline">
                    {theme.contactEmail}
                  </a>
                </li>
                {theme.contactPhone ? (
                  <li className="flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 shrink-0" style={accentText} />
                    <a href={`tel:${theme.contactPhone}`} className="hover:underline">
                      {theme.contactPhone}
                    </a>
                  </li>
                ) : null}
              </ul>
            </div>
          </div>
          <div className="container mx-auto mt-12 pt-6 border-t border-white/10 text-center text-slate-500 text-[10px]">
            <p>
              © {new Date().getFullYear()} {theme.storeDisplayName}. All rights reserved.
            </p>
          </div>
        </footer>
      )}
    </div>
  );
}
